// src/pages/MiInventarioPage.tsx
import { useState, useEffect } from 'react'
import { api } from '../services/api'
import { Package, Search, RefreshCw } from 'lucide-react'

interface InventarioItem {
  id: number
  id_producto: number
  producto_nombre: string
  codigo_barras: string
  cantidad_asignada: number
  cantidad_disponible: number
  precio_venta_a: number
  precio_venta_b: number
  precio_venta_c: number
  iva: string
  fecha_asignacion: string
}

export default function MiInventarioPage() {
  const [inventario, setInventario] = useState<InventarioItem[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(true)

  useEffect(() => {
    cargarInventario()
  }, [search])

  useEffect(() => {
    if (!autoRefresh) return
    const interval = setInterval(cargarInventario, 10000) // Cada 10 segundos
    return () => clearInterval(interval)
  }, [autoRefresh, search])

  const cargarInventario = async () => {
    try {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      
      const { data } = await api.get(`/inventario/mi-inventario?${params}`)
      setInventario(data.data)
    } catch (error) {
      console.error('Error cargando inventario:', error)
    } finally {
      setLoading(false)
    }
  }

  const totalDisponible = inventario.reduce((sum, item) => sum + item.cantidad_disponible, 0)
  const totalVendido = inventario.reduce((sum, item) => sum + (item.cantidad_asignada - item.cantidad_disponible), 0)

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="w-6 h-6" />
          Mi Inventario
        </h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4"
            />
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin text-green-600' : 'text-gray-400'}`} />
            Auto-actualizar
          </label>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Total Productos</p>
          <p className="text-2xl font-bold">{inventario.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Unidades Disponibles</p>
          <p className="text-2xl font-bold text-green-600">{totalDisponible}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Unidades Vendidas</p>
          <p className="text-2xl font-bold text-blue-600">{totalVendido}</p>
        </div>
      </div>

      {/* Búsqueda */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o código de barras..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
      </div>

      {/* Lista de Productos */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Producto</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-500">Asignado</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-500">Disponible</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-500">Vendido</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Precio</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  Cargando...
                </td>
              </tr>
            ) : inventario.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No tienes productos asignados</p>
                  <p className="text-sm">Contacta al administrador para que te asigne inventario</p>
                </td>
              </tr>
            ) : (
              inventario.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium">{item.producto_nombre}</p>
                    <p className="text-sm text-gray-500">{item.codigo_barras}</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-medium">{item.cantidad_asignada}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-medium px-2 py-1 rounded ${
                      item.cantidad_disponible <= 0 ? 'bg-red-100 text-red-700' :
                      item.cantidad_disponible <= 5 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {item.cantidad_disponible}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-medium text-blue-600">
                      {item.cantidad_asignada - item.cantidad_disponible}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className="font-medium">${item.precio_venta_a.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">IVA {item.iva}%</p>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
